/**
 * Browser-side "Did you mean" using vocabulary from GET /api/quick-search-spell-dictionary (loaded once).
 */

const CACHE_KEY = "qs_spell_dict_v4";
const CACHE_TS_KEY = "qs_spell_dict_v4_ts";
const CACHE_TTL_MS = 86400000;
const LOG_PREFIX = "[QuickSearch Spell]";

function log(...args) {
  console.log(LOG_PREFIX, ...args);
}

function logWarn(...args) {
  console.warn(LOG_PREFIX, ...args);
}

let dictionaryData = null;
let dictionaryLoadPromise = null;

function dictionaryStats(data) {
  if (!data || !data.by_len) {
    return {wordBuckets: 0, words: 0, phraseBuckets: 0, phrases: 0};
  }
  const byLen = data.by_len;
  const phrases = data.phrases_by_len || {};
  let words = 0;
  Object.keys(byLen).forEach((k) => {
    words += (byLen[k] || []).length;
  });
  let phraseCount = 0;
  Object.keys(phrases).forEach((k) => {
    phraseCount += (phrases[k] || []).length;
  });
  return {
    wordBuckets: Object.keys(byLen).length,
    words,
    phraseBuckets: Object.keys(phrases).length,
    phrases: phraseCount,
  };
}

function wordsByLength(data, len) {
  if (!data || !data.by_len) {
    return [];
  }
  return data.by_len[len] || data.by_len[String(len)] || [];
}

function phrasesByLength(data, len) {
  if (!data || !data.phrases_by_len) {
    return [];
  }
  return data.phrases_by_len[len] || data.phrases_by_len[String(len)] || [];
}

function splitTokens(query) {
  const trimmed = (query || "").trim();
  if (!trimmed) {
    return [];
  }
  try {
    const parts = trimmed.split(/[^\p{L}\p{N}]+/u);
    if (parts && parts.length > 0) {
      return parts.filter(Boolean);
    }
  } catch (e) {
    logWarn("splitTokens: unicode split failed, using fallback", e);
  }
  return trimmed.split(/[^a-zA-Z0-9]+/).filter(Boolean);
}

function normalizeDictionaryData(data) {
  if (!data || typeof data !== "object") {
    return null;
  }
  if (data.by_len && typeof data.by_len === "object") {
    const flat = [];
    Object.keys(data.by_len).forEach((key) => {
      const bucket = data.by_len[key];
      if (Array.isArray(bucket)) {
        bucket.forEach((word) => {
          const lower = String(word).toLowerCase();
          if (!/^\d+$/.test(lower) && /[a-z]/i.test(lower)) {
            flat.push(lower);
          }
        });
      }
    });
    data._wordFlat = flat;
    return data;
  }
  logWarn("normalizeDictionaryData: missing by_len", Object.keys(data));
  return null;
}

function tokenLength(token) {
  return [...token].length;
}

function toLowerAscii(token) {
  return String(token).toLowerCase();
}

function isAcceptableSpellCandidate(typedToken, candidate) {
  if (!candidate || !typedToken) {
    return false;
  }
  if (/^\d+$/.test(candidate)) {
    return false;
  }
  if (!/[a-z]/i.test(candidate)) {
    return false;
  }
  const typedFirst = typedToken[0] || "";
  const candidateFirst = candidate[0] || "";
  if (/[a-z]/i.test(typedFirst) && /[a-z]/i.test(candidateFirst) && typedFirst !== candidateFirst) {
    return false;
  }
  return true;
}

function isAsciiPrintable(value) {
  return /^[\x20-\x7E]+$/.test(value);
}

function levenshtein(a, b) {
  if (!isAsciiPrintable(a) || !isAsciiPrintable(b)) {
    return null;
  }
  if (a.length > 255 || b.length > 255) {
    return null;
  }
  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix = [];
  for (let i = 0; i < rows; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j < cols; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[a.length][b.length];
}

function similarTextPercent(a, b) {
  const d = levenshtein(a, b);
  if (d === null) {
    return 0;
  }
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) {
    return 100;
  }
  return ((maxLen - d) / maxLen) * 100;
}

function maxEditsForWordLength(len) {
  if (len < 4) {
    return 0;
  }
  if (len <= 5) {
    return 1;
  }
  if (len <= 7) {
    return 2;
  }
  if (len <= 11) {
    return 3;
  }
  return 4;
}

function candidateAppearsWithContextTokens(candidate, contextTokens, data) {
  if (!contextTokens.length) {
    return false;
  }
  const buckets = data.phrases_by_len || {};
  for (const key of Object.keys(buckets)) {
    const phrases = buckets[key] || [];
    for (const phrase of phrases) {
      if (phrase.includes(candidate)) {
        for (const context of contextTokens) {
          const ctx = toLowerAscii(context.trim());
          if (ctx.length >= 4 && phrase.includes(ctx)) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

function preferSpellCandidate(typedToken, a, b, contextTokens, data) {
  const pctA = similarTextPercent(typedToken, a);
  const pctB = similarTextPercent(typedToken, b);
  if (pctA !== pctB) {
    return pctB - pctA;
  }
  const aContext = candidateAppearsWithContextTokens(a, contextTokens, data);
  const bContext = candidateAppearsWithContextTokens(b, contextTokens, data);
  if (aContext !== bContext) {
    return (bContext ? 1 : 0) - (aContext ? 1 : 0);
  }
  const aFirst = (a[0] || "") === (typedToken[0] || "");
  const bFirst = (b[0] || "") === (typedToken[0] || "");
  if (aFirst !== bFirst) {
    return (bFirst ? 1 : 0) - (aFirst ? 1 : 0);
  }
  return a.localeCompare(b);
}

function bestDictionaryWord(lowerToken, data, contextTokens = []) {
  const tokenLen = tokenLength(lowerToken);
  const maxEdits = maxEditsForWordLength(tokenLen);
  const exactBucket = wordsByLength(data, tokenLen);
  for (const candidate of exactBucket) {
    if (candidate === lowerToken) {
      return candidate;
    }
  }

  let bestWord = null;
  let bestDistance = Number.MAX_SAFE_INTEGER;
  const minLen = Math.max(4, tokenLen - maxEdits);
  const maxLen = tokenLen + maxEdits;

  for (let len = minLen; len <= maxLen; len++) {
    for (const candidate of wordsByLength(data, len)) {
      if (!isAcceptableSpellCandidate(lowerToken, candidate)) {
        continue;
      }
      if (candidate === lowerToken) {
        return candidate;
      }
      const distance = levenshtein(lowerToken, candidate);
      if (distance === null || distance > maxEdits) {
        continue;
      }
      if (
          bestWord === null
          || distance < bestDistance
          || (distance === bestDistance && preferSpellCandidate(lowerToken, candidate, bestWord, contextTokens, data) < 0)
      ) {
        bestDistance = distance;
        bestWord = candidate;
      }
    }
  }

  if (bestWord === null && Array.isArray(data._wordFlat) && data._wordFlat.length) {
    for (const candidate of data._wordFlat) {
      if (!isAcceptableSpellCandidate(lowerToken, candidate)) {
        continue;
      }
      if (candidate === lowerToken) {
        return candidate;
      }
      const cLen = candidate.length;
      if (cLen < minLen || cLen > maxLen) {
        continue;
      }
      const distance = levenshtein(lowerToken, candidate);
      if (distance === null || distance > maxEdits) {
        continue;
      }
      if (
          bestWord === null
          || distance < bestDistance
          || (distance === bestDistance && preferSpellCandidate(lowerToken, candidate, bestWord, contextTokens, data) < 0)
      ) {
        bestDistance = distance;
        bestWord = candidate;
      }
    }
  }

  return bestWord;
}

function suggestByTokenCorrections(query, data) {
  const tokens = splitTokens(query);
  if (!tokens.length) {
    return null;
  }

  const significantTokens = tokens
      .filter((token) => tokenLength(token) >= 4)
      .map((token) => toLowerAscii(token));

  let changed = false;
  const out = [];

  for (const token of tokens) {
    if (tokenLength(token) < 4) {
      out.push(token);
      continue;
    }
    const lower = toLowerAscii(token);
    const context = significantTokens.filter((other) => other !== lower);
    const match = bestDictionaryWord(lower, data, context);
    if (
        match !== null
        && toLowerAscii(match) !== lower
        && isAcceptableSpellCandidate(lower, match)
    ) {
      out.push(match);
      changed = true;
    } else {
      out.push(token);
    }
  }

  return changed ? out.join(" ") : null;
}

function allSignificantTokensAreKnownDictionaryWords(query, data) {
  const tokens = splitTokens(query);
  let checked = 0;
  for (const token of tokens) {
    if (tokenLength(token) < 4) {
      continue;
    }
    checked++;
    const lower = toLowerAscii(token);
    const match = bestDictionaryWord(lower, data);
    if (match === null || toLowerAscii(match) !== lower) {
      return false;
    }
  }
  return checked > 0;
}

function collapseWhitespace(value) {
  return value.trim().replace(/\s+/g, " ");
}

function shouldTryPhraseMatch(normSpaceLower) {
  if (!normSpaceLower) {
    return false;
  }
  if (normSpaceLower.includes(" ")) {
    return normSpaceLower.length >= 8;
  }
  return normSpaceLower.length >= 12;
}

function suggestWholePhrase(normSpaceLower, data) {
  if (!isAsciiPrintable(normSpaceLower)) {
    return null;
  }
  const len = normSpaceLower.length;
  if (len < 8 || len > 255) {
    return null;
  }

  const maxD = Math.max(2, Math.min(14, Math.floor(len * 0.48)));
  let best = null;
  let bestD = Number.MAX_SAFE_INTEGER;
  const q0 = normSpaceLower[0] || "";

  for (let pl = Math.max(8, len - maxD); pl <= len + maxD; pl++) {
    for (const phrase of phrasesByLength(data, pl)) {
      if (!/[a-z]/i.test(phrase) || /^\d[\d\s]*$/.test(phrase.trim())) {
        continue;
      }
      if ((phrase[0] || "") !== q0) {
        continue;
      }
      if (similarTextPercent(normSpaceLower, phrase) < 55) {
        continue;
      }
      const d = levenshtein(normSpaceLower, phrase);
      if (d === null || d > maxD) {
        continue;
      }
      const wordCountPenalty =
          normSpaceLower.split(" ").length !== phrase.split(" ").length ? 2 : 0;
      const rank = d + wordCountPenalty;
      if (rank < bestD || (rank === bestD && phrase.localeCompare(best || "") < 0)) {
        bestD = rank;
        best = phrase;
      }
    }
  }

  if (best === null || bestD === 0) {
    return null;
  }
  return best;
}

export function suggestFromQuery(query, data) {
  const trimmed = (query || "").trim();
  if (!trimmed || !data) {
    return null;
  }

  const normLower = toLowerAscii(trimmed);
  const normSpace = collapseWhitespace(normLower);
  const tokens = splitTokens(trimmed);

  log("suggestFromQuery: tokens", {query: trimmed, tokens});

  if (shouldTryPhraseMatch(normSpace) && !allSignificantTokensAreKnownDictionaryWords(trimmed, data)) {
    const phraseHit = suggestWholePhrase(normSpace, data);
    if (phraseHit !== null && phraseHit !== normSpace) {
      log("suggestFromQuery: phrase match", {query: trimmed, phraseHit});
      return phraseHit;
    }
  }

  const tokenSuggestion = suggestByTokenCorrections(trimmed, data);
  if (tokenSuggestion !== null) {
    log("suggestFromQuery: token match", {query: trimmed, tokenSuggestion});
    return tokenSuggestion;
  }

  return null;
}

export function hasMisspelledWord(query, data) {
  const trimmed = (query || "").trim();
  if (!trimmed || !data) {
    return false;
  }

  for (const token of splitTokens(trimmed)) {
    if (tokenLength(token) < 4) {
      continue;
    }
    const lower = toLowerAscii(token);
    const match = bestDictionaryWord(lower, data);
    if (match === null || toLowerAscii(match) === lower) {
      continue;
    }
    const distance = levenshtein(lower, toLowerAscii(match));
    if (distance === null) {
      continue;
    }
    const maxEdits = maxEditsForWordLength(tokenLength(token));
    if (distance > 0 && distance <= maxEdits) {
      return true;
    }
  }

  return false;
}

export function resolveDidYouMean(query, data, debug = true) {
  const trimmed = (query || "").trim();
  if (trimmed.length < 4) {
    if (debug) {
      log("resolveDidYouMean: skipped (query < 4 chars)", {query: trimmed});
    }
    return null;
  }
  if (!data) {
    if (debug) {
      logWarn("resolveDidYouMean: no dictionary loaded");
    }
    return null;
  }

  const tokens = splitTokens(trimmed);
  const alternate = suggestFromQuery(trimmed, data);
  const misspelled = hasMisspelledWord(trimmed, data);
  const sivlerProbe = bestDictionaryWord("sivler", data, ["park"]);

  if (debug) {
    log("resolveDidYouMean", {
      query: trimmed,
      tokens,
      alternate,
      misspelled,
      sivlerProbe,
      stats: dictionaryStats(data),
    });
  }

  if (alternate === null || toLowerAscii(alternate) === toLowerAscii(trimmed)) {
    if (debug) {
      log("resolveDidYouMean: no alternate suggestion");
    }
    return null;
  }
  if (/^\d[\d\s]*$/.test(alternate.trim())) {
    if (debug) {
      logWarn("resolveDidYouMean: rejected numeric suggestion", alternate);
    }
    return null;
  }

  if (debug) {
    log("resolveDidYouMean: showing", alternate);
  }
  return alternate;
}

export function getCachedDictionary() {
  return dictionaryData ? normalizeDictionaryData(dictionaryData) : null;
}

export function loadSpellDictionary(fetchDictionary) {
  if (dictionaryData) {
    dictionaryData = normalizeDictionaryData(dictionaryData);
    log("loadSpellDictionary: using in-memory cache", dictionaryStats(dictionaryData));
    return Promise.resolve(dictionaryData);
  }
  if (dictionaryLoadPromise) {
    log("loadSpellDictionary: waiting for in-flight load");
    return dictionaryLoadPromise;
  }

  log("loadSpellDictionary: starting");

  dictionaryLoadPromise = new Promise((resolve) => {
    try {
      const ts = Number(sessionStorage.getItem(CACHE_TS_KEY) || 0);
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw && Date.now() - ts < CACHE_TTL_MS) {
        dictionaryData = normalizeDictionaryData(JSON.parse(raw));
        log("loadSpellDictionary: sessionStorage cache hit", dictionaryStats(dictionaryData));
        resolve(dictionaryData);
        return;
      }
      log("loadSpellDictionary: sessionStorage miss or expired");
    } catch (e) {
      logWarn("loadSpellDictionary: sessionStorage read failed", e);
    }

    fetchDictionary()
        .then((data) => {
          dictionaryData = normalizeDictionaryData(data || null);
          if (dictionaryData) {
            log("loadSpellDictionary: API ok", dictionaryStats(dictionaryData));
            try {
              sessionStorage.setItem(CACHE_KEY, JSON.stringify(dictionaryData));
              sessionStorage.setItem(CACHE_TS_KEY, String(Date.now()));
            } catch (e) {
              logWarn("loadSpellDictionary: sessionStorage write failed", e);
            }
          } else {
            logWarn("loadSpellDictionary: API returned empty data");
          }
          resolve(dictionaryData);
        })
        .catch((err) => {
          logWarn("loadSpellDictionary: API failed", err);
          dictionaryData = null;
          resolve(null);
        });
  });

  return dictionaryLoadPromise;
}
